import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import * as Sentry from '@sentry/browser';

import { CURRENCIES } from '../../utils/currency';
import { getCurrencySymbol } from '../../utils/formatters';
import {
  ContractContent,
  Wrapper,
  HIGHLIGHT_CLASSNAME,
  AgreementContainer,
  StyledListItem,
  Title,
  StyledContactLink,
  StyledList,
  StyledParagraph,
} from './styles';
import texts from './texts.json';

const ContractDetails = ({
  contractTerms,
  coachConditions,
  isDarkTheme,
  isAmendment,
  className,
  userId,
  isCheckoutPageview,
}) => {
  useEffect(() => {
    // We should never reach this situation, so we want to report it right away to Sentry.
    if (userId && (!contractTerms || !contractTerms.length)) {
      Sentry.captureException(new Error(texts.noContractTerms), {
        extra: {
          userId,
        },
      });
    }
  }, [
    contractTerms,
    userId,
  ]);

  const renderTerms = useCallback(() => {
    if (!contractTerms || !contractTerms.length) {
      return texts.noContractTerms;
    }
    let baseTerms = contractTerms;
    let pauseClause = null;
    // Split the terms if it's an amendment
    if (isAmendment && contractTerms.length > 0) {
      baseTerms = contractTerms.slice(0, -1);
      pauseClause = <p>{contractTerms[contractTerms.length - 1]}</p>;
    }

    const datePattern = /\b(?:\w+)\s\d{1,2},\s\d{4}\b/g;

    // Build the price pattern based on the currencies we support.
    const currenciesPattern = CURRENCIES.map((currency) => {
      const symbol = getCurrencySymbol(currency.code);

      // If the currency symbol includes the dollar sign, then we need to escape it for the regex to work.
      return symbol.replaceAll('$', '\\$');
    }).join('|');

    const pricePattern = new RegExp(`(${currenciesPattern})\\d+(\\.\\d{2})?\\b`, 'g');
    // eslint-disable-next-line max-len
    const paymentDayPattern = /\d{1,2}(?:st|nd|rd|th)(?:\sof each month|\severy\s\d+\smonths)?(?:\s\(except in February\))?/g;
    const monthPattern = /\d+\smonth\(s\)/g;
    const wrapperPattern = `<span class="${HIGHLIGHT_CLASSNAME}">$&</span>`;

    return {
      baseTerms: baseTerms.map((paragraph) => {
        const highlightedText = paragraph
          .replaceAll(datePattern, wrapperPattern)
          .replaceAll(pricePattern, wrapperPattern)
          .replaceAll(monthPattern, wrapperPattern)
          .replaceAll(paymentDayPattern, wrapperPattern);
        return <p key={paragraph}>{ReactHtmlParser(highlightedText)}</p>;
      }),
      pauseClause,
    };
  }, [contractTerms, isAmendment]);

  const { baseTerms, pauseClause } = renderTerms();

  return (
    <Wrapper className={className} $isCheckoutPageview={isCheckoutPageview}>
      <ContractContent $isDarkTheme={isDarkTheme} $isCheckoutPageview={isCheckoutPageview}>
        {baseTerms}
      </ContractContent>
      {!!coachConditions && (
        <ContractContent $isDarkTheme={isDarkTheme} $isCheckoutPageview={isCheckoutPageview}>
          {/* Adding ReactHtmlParser in case the custom conditions have html tags like <p>, and <br /> */}
          {ReactHtmlParser(coachConditions)}
        </ContractContent>
      )}
      {isCheckoutPageview && (
        <AgreementContainer $isCheckoutPageview={isCheckoutPageview}>
          <Title $isDarkTheme={isDarkTheme}>{ReactHtmlParser(texts.agreementTitle)}</Title>
          <StyledList>
            <StyledListItem $isCheckoutPageview={isCheckoutPageview}>{texts.agreeToTheTerms}</StyledListItem>
            <StyledListItem $isCheckoutPageview={isCheckoutPageview}>{texts.paymentsNonRefundable}</StyledListItem>
          </StyledList>
          <StyledParagraph>
            {texts.contactUsText}
            <StyledContactLink href="mailto:support@avida.life" $isDarkTheme={isDarkTheme}>
              {texts.contactUsLink}
            </StyledContactLink>
          </StyledParagraph>
        </AgreementContainer>
      )}
      {!!isAmendment && (
        <ContractContent>
          {pauseClause}
        </ContractContent>
      )}
    </Wrapper>
  );
};

ContractDetails.propTypes = {
  contractTerms: PropTypes.array.isRequired,
  coachConditions: PropTypes.string,
  className: PropTypes.string,
  userId: PropTypes.string,
  isDarkTheme: PropTypes.bool,
  isCheckoutPageview: PropTypes.bool,
  isAmendment: PropTypes.bool,
};

ContractDetails.defaultProps = {
  className: '',
  coachConditions: '',
  userId: '',
  isDarkTheme: false,
  isCheckoutPageview: false,
  isAmendment: false,
};

export default ContractDetails;
